package com.appsyde.shared.presentation.screen.component.ticket

import com.appsyde.shared.app.presentation.ticket.TicketDetectedFromMemory
import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.model.parseKeysJsonToSet
import com.appsyde.shared.modules.ticket.domain.model.Ticket
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import com.appsyde.shared.modules.ticket.domain.model.ExtractStatus
import com.appsyde.shared.presentation.screen.component.message.helper.byKey
import com.appsyde.shared.presentation.screen.component.message.helper.computeHeaderSubtitle
import com.appsyde.shared.presentation.screen.component.message.helper.computeHeaderTitle
import com.appsyde.shared.presentation.screen.component.message.helper.IntentFamily
import com.appsyde.shared.presentation.screen.component.message.helper.intentFamily
import com.appsyde.shared.presentation.screen.component.message.helper.intentLabelFr
import com.appsyde.shared.presentation.screen.component.message.helper.intentToIcon
import com.appsyde.shared.presentation.screen.component.message.mapper.uiLabelForKey

/** Seuil "récent" pour le tri 5 groupes : 48 h (dernier message client). */
private const val RECENT_THRESHOLD_MS = 48L * 60 * 60 * 1000

/**
 * Tri en 5 groupes : Mes récents → Libres récents → Mes anciens → Libres anciens → Autres.
 * Dans chaque groupe : tri par dernier message (lastInboundSentAtMs ?: updatedAtMs) décroissant.
 */
fun sortTicketsFiveGroups(
    tickets: List<Ticket>,
    currentUserAccountId: String?,
    nowMs: Long
): List<Ticket> {
    if (currentUserAccountId.isNullOrBlank()) return tickets
    val activityDesc = compareByDescending<Ticket> { it.lastInboundSentAtMs ?: it.updatedAtMs }.thenBy { it.ticketId }
    fun activityMs(t: Ticket) = t.lastInboundSentAtMs ?: t.updatedAtMs
    fun isRecent(t: Ticket) = (nowMs - activityMs(t)) < RECENT_THRESHOLD_MS
    val mineRecent = mutableListOf<Ticket>()
    val freeRecent = mutableListOf<Ticket>()
    val mineOld = mutableListOf<Ticket>()
    val freeOld = mutableListOf<Ticket>()
    val other = mutableListOf<Ticket>()
    for (t in tickets) {
        val mine = t.assignedTo?.accountUserId == currentUserAccountId
        val free = t.assignedTo == null
        val recent = isRecent(t)
        when {
            mine && recent -> mineRecent.add(t)
            free && recent -> freeRecent.add(t)
            mine && !recent -> mineOld.add(t)
            free && !recent -> freeOld.add(t)
            else -> other.add(t)
        }
    }
    mineRecent.sortWith(activityDesc)
    freeRecent.sortWith(activityDesc)
    mineOld.sortWith(activityDesc)
    freeOld.sortWith(activityDesc)
    other.sortWith(activityDesc)
    return mineRecent + freeRecent + mineOld + freeOld + other
}

/**
 * Âge relatif pour la liste (ex: "il y a 12 min", "il y a 1 h", "Hier").
 * Accepte updatedAt en ms ou en secondes (Unix) : si la valeur est < 1e12, on la traite comme secondes.
 */
fun formatTicketAge(updatedAtMs: Long, nowMs: Long): String {
    val updatedMs = if (updatedAtMs in 1L..1_000_000_000_000L) updatedAtMs * 1000 else updatedAtMs
    val diffMs = (nowMs - updatedMs).coerceAtLeast(0)
    val diffMin = diffMs / (60 * 1000)
    val diffHours = diffMs / (60 * 60 * 1000)
    val diffDays = diffMs / (24 * 60 * 60 * 1000)
    return when {
        diffMin < 1 -> "À l'instant"
        diffMin < 60 -> "il y a $diffMin min"
        diffHours < 24 -> "il y a $diffHours h"
        diffDays == 1L -> "Hier"
        diffDays < 7 -> "il y a $diffDays j"
        else -> "il y a ${diffDays / 7} sem"
    }
}

/** Normalise l'intent (snake_case) pour intentLabelFr / intentFamily. */
private fun normalizeIntentForHeader(intent: String?): String? =
    intent?.trim()?.lowercase()?.replace(" ", "_")?.takeIf { it.isNotBlank() }

/** Intents liés remboursement / traitement ecom (pour état "En traitement remboursement"). */
private fun isRefundRelatedIntent(intent: String?): Boolean =
    intent?.trim()?.lowercase() in setOf(
        "damaged_product", "product_damaged", "missing_item", "wrong_item",
        "refund_request", "return_request", "replacement_request"
    )

/**
 * Calcule l'état opérationnel (label + kind) pour affichage dans la sidebar "État du dossier" ou la carte.
 * Réutilisé par TicketsSidebar et TicketToRowUi.
 */
fun computeOperationalState(
    statusNorm: String,
    missingKeysList: List<String>,
    lastFromUs: Boolean,
    normalizedIntent: String?
): Pair<String, String> = when {
    statusNorm == TicketStatus.WAITING -> "En attente client" to "waiting"
    statusNorm == TicketStatus.CLOSED -> "Fermé" to "closed"
    statusNorm == TicketStatus.OPEN && missingKeysList.isEmpty() && lastFromUs -> "Prêt à clôturer" to "ready"
    (statusNorm == TicketStatus.OPEN || statusNorm == TicketStatus.WAITING) && isRefundRelatedIntent(normalizedIntent) -> "En traitement remboursement" to "refund"
    else -> "À traiter" to "action"
}

/** Icône pour l'état opérationnel (réutilisé carte + sidebar). */
fun operationalStateIcon(kind: String): String = when (kind) {
    "waiting" -> "⚠️"
    "action" -> "🟢"
    "ready" -> "🔵"
    "refund" -> "🟣"
    else -> "•"
}

/** Dernier message = nous si outbound après ou égal au dernier inbound. */
private fun isLastMessageFromUs(lastInboundSentAtMs: Long?, lastOutboundSentAtMs: Long?): Boolean =
    lastOutboundSentAtMs != null && (lastInboundSentAtMs == null || lastOutboundSentAtMs >= lastInboundSentAtMs)

private const val MS_PER_DAY = 24L * 60 * 60 * 1000

/**
 * Règles métier (front only) : label + clé couleur pour la ligne ticket.
 * Source : isClosed, lastMessageDirection, lastMessageAt, missingCount, toConfirmCount, now.
 */
private fun computeBusinessStatus(
    isClosed: Boolean,
    lastMessageDirectionOutbound: Boolean,
    lastMessageAtMs: Long,
    nowMs: Long,
    missingCount: Int,
    toConfirmCount: Int
): Pair<String, String> {
    val ageDays = ((nowMs - lastMessageAtMs) / MS_PER_DAY).toLong().coerceAtLeast(0)
    val sinceSuffix = if (ageDays >= 1) " depuis $ageDays jours" else ""

    return when {
        isClosed -> "Traité" to "traite"
        !lastMessageDirectionOutbound -> when {
            ageDays == 0L -> "À traiter" to "a_traiter_neutral"
            else -> "À traiter$sinceSuffix" to "a_traiter"
        }
        lastMessageDirectionOutbound && (missingCount > 0 || toConfirmCount > 0) -> when {
            ageDays == 0L -> "À vérifier" to "a_verifier"
            else -> "À vérifier$sinceSuffix" to "a_verifier"
        }
        else -> when {
            ageDays == 0L -> "En attente client" to "en_attente_client"
            else -> "En attente client$sinceSuffix" to "en_attente_client"
        }
    }
}

/** Libellé court "problème" depuis issue_type (mémoire) ou intent. */
private fun problemLabelFromMemoryOrIntent(m: Map<String, String>, intentLabel: String): String? {
    val issueType = m["issue_type"]?.trim()?.lowercase() ?: return intentLabel.takeIf { it.isNotBlank() && it != "—" }
    return when (issueType) {
        "damaged" -> "Produit cassé"
        "missing_item" -> "Article manquant"
        "wrong_item" -> "Mauvais article"
        else -> intentLabel.takeIf { it.isNotBlank() && it != "—" }
    }
}

/**
 * Calcule le titre de la ligne comme le RespondCardHeader, à partir de l'extract IA.
 */
fun computeRowTitleFromExtract(ticket: Ticket, extract: AiExtractV2): String =
    computeHeaderTitle(
        intentLabel = intentLabelFr(extract.intent),
        subject = ticket.subject?.trim()?.takeIf { it.isNotBlank() } ?: ticket.stateSummary?.trim()?.takeIf { it.isNotBlank() },
        family = intentFamily(extract.intent),
        m = extract.detected.byKey()
    )

private data class RowTitleInputs(
    val normalizedIntent: String?,
    val intentLabel: String,
    val intentIcon: String,
    val family: IntentFamily,
    val m: Map<String, String>,
    val computedTitle: String
)

/**
 * Map un [Ticket] domaine vers [TicketRowUi] pour la liste.
 * Si [extract] est fourni, titre + intent + icône viennent de l'extract (aligné Respond).
 * Sinon utilise [Ticket.currentIntent] / detectedMemoryJson (données liste).
 * [currentUserAccountId] sert à définir [TicketRowUi.rowVariant] (mine / free / other).
 */
fun Ticket.toTicketRowUi(nowMs: Long, extract: AiExtractV2? = null, currentUserAccountId: String? = null): TicketRowUi {
    val missingSet = parseKeysJsonToSet(missingKeysJson)
    val toConfirmSet = parseKeysJsonToSet(toConfirmKeysJson)

    val inputs = if (extract != null) {
        val ni = extract.intent?.trim()?.lowercase()?.replace(" ", "_")?.takeIf { it.isNotBlank() }
        val il = intentLabelFr(extract.intent) ?: extract.intent?.takeIf { it.isNotBlank() } ?: "—"
        val fam = intentFamily(extract.intent)
        val detected = extract.detected.byKey()
        val title = computeRowTitleFromExtract(this, extract)
        RowTitleInputs(ni, il, intentToIcon(extract.intent), fam, detected, title)
    } else {
        val normalizedIntent = normalizeIntentForHeader(currentIntent)
        val intentLabelForTitle = intentLabelFr(normalizedIntent)
            ?: normalizedIntent?.replace("_", " ")?.replaceFirstChar { it.uppercase() }
        val intentLabel = intentLabelFr(normalizedIntent) ?: currentIntent?.takeIf { it.isNotBlank() } ?: "—"
        val family = intentFamily(normalizedIntent)
        val detectedFields = TicketDetectedFromMemory.parseAndEnrich(detectedMemoryJson, clientEmail)
        val m = detectedFields.byKey()
        // Sans extract : ne pas utiliser subject pour le titre (évite flash quand l'extract arrive).
        val computedTitle = computeHeaderTitle(
            intentLabel = intentLabelForTitle,
            subject = null,
            family = family,
            m = m
        )
        RowTitleInputs(normalizedIntent, intentLabel, intentToIcon(currentIntent), family, m, computedTitle)
    }

    val normalizedIntent = inputs.normalizedIntent
    val intentLabel = inputs.intentLabel
    val family = inputs.family
    val m = inputs.m
    val computedTitle = inputs.computedTitle

    val missingKeysList = missingSet.map { it.trim().lowercase() }.filter { it.isNotBlank() }.sorted()
    val toConfirmKeysList = toConfirmSet.map { it.trim().lowercase() }.filter { it.isNotBlank() }.sorted()

    val topMissingLabel = missingKeysList.firstOrNull()?.let { uiLabelForKey(it) }
    val topConfirmLabel = toConfirmKeysList.firstOrNull()?.let { uiLabelForKey(it) }

    val missingPillText = when {
        missingKeysList.isEmpty() -> null
        topMissingLabel.isNullOrBlank() -> "Manque : ${missingKeysList.first()}"
        missingKeysList.size == 1 -> "Manque : $topMissingLabel"
        else -> "Manque : $topMissingLabel +${missingKeysList.size - 1}"
    }

    val confirmPillText = when {
        missingKeysList.isNotEmpty() -> null // missing > confirm
        toConfirmKeysList.isEmpty() -> null
        topConfirmLabel.isNullOrBlank() -> "${toConfirmKeysList.size} à confirmer"
        toConfirmKeysList.size == 1 -> "Confirmer : $topConfirmLabel"
        else -> "Confirmer : $topConfirmLabel +${toConfirmKeysList.size - 1}"
    }

    val subtitleItems = computeHeaderSubtitle(
        m = m,
        missingKeys = missingKeysList,
        toConfirmKeys = toConfirmKeysList,
        titleText = computedTitle,
        currentIntent = normalizedIntent
    )

    val statusNorm = status.uppercase().takeIf { it in setOf(TicketStatus.OPEN, TicketStatus.WAITING, TicketStatus.CLOSED) } ?: status
    val lastFromUs = isLastMessageFromUs(lastInboundSentAtMs, lastOutboundSentAtMs)
    val hasAnyMessage = lastInboundSentAtMs != null || lastOutboundSentAtMs != null

    val (operationalStateLabel, operationalStateKind) = computeOperationalState(statusNorm, missingKeysList, lastFromUs, normalizedIntent)

    val problemBadge = problemLabelFromMemoryOrIntent(m, intentLabel)
    val missingBadges = missingKeysList.take(1).map { key ->
        "Manque : ${uiLabelForKey(key) ?: key}"
    }
    val lastMessageFrom = if (hasAnyMessage) if (lastFromUs) "Vous" else "Client" else null

    val lastMessageAtMs = listOfNotNull(lastInboundSentAtMs, lastOutboundSentAtMs).maxOrNull() ?: updatedAtMs
    val ageDays = ((nowMs - lastMessageAtMs) / MS_PER_DAY).toLong().coerceAtLeast(0)
    val (businessStatusLabel, businessStatusColorKey) = computeBusinessStatus(
        isClosed = statusNorm == TicketStatus.CLOSED,
        lastMessageDirectionOutbound = lastFromUs,
        lastMessageAtMs = lastMessageAtMs,
        nowMs = nowMs,
        missingCount = missingSet.size,
        toConfirmCount = toConfirmSet.size
    )
    val showRelativeAge = statusNorm == TicketStatus.CLOSED || ageDays == 0L

    val extractInProgress = extractStatus == ExtractStatus.PENDING || extractStatus == ExtractStatus.RUNNING

    return TicketRowUi(
        ticketId = ticketId,
        status = statusNorm,
        summary = computedTitle,
        subtitleItems = subtitleItems,
        client = clientEmail?.takeIf { it.isNotBlank() } ?: "—",
        age = formatTicketAge(lastInboundSentAtMs ?: updatedAtMs, nowMs),
        intent = intentLabel,
        intentIcon = inputs.intentIcon,
        priority = currentPriority?.takeIf { it.isNotBlank() } ?: "Normale",
        missingCount = missingSet.size,
        confirmCount = toConfirmSet.size,
        unread = !isRead,
        missingPillText = missingPillText,
        confirmPillText = confirmPillText,
        operationalStateLabel = operationalStateLabel,
        operationalStateKind = operationalStateKind,
        problemBadge = problemBadge,
        missingBadges = missingBadges,
        lastMessageFrom = lastMessageFrom,
        businessStatusLabel = businessStatusLabel,
        businessStatusColorKey = businessStatusColorKey,
        showRelativeAge = showRelativeAge,
        extractInProgress = extractInProgress,
        assigneeDisplayName = assignedTo?.displayName?.takeIf { it.isNotBlank() },
        rowVariant = when {
            assignedTo == null -> "free"
            assignedTo.accountUserId == currentUserAccountId -> "mine"
            else -> "other"
        }
    )
}
